/**
 * Post Show Setup
 *
 * Initializes post detail page collaborators and shared state wiring.
 */

import { PostShowApiService } from "./post-show-api";
import { PostShowHandler } from "./post-show-handler";
import { bindPostShowPageReadScroll, PostShowListener, unbindPostShowPageReadScroll } from "./post-show-listener";
import { PostShowStateHandler } from "./post-show-state-handler";
import { postShowForm } from "./post-show-form";
import { phpAuthUser } from "../../core/php-data-access";
import { SNSPreviewManagerSetup } from "../../sns/preview-manager/sns-preview-manager-setup";
import { Roles } from "../../constants/roles";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { initAffiliateCopyButtons } from "../shared/affiliate-copy-handler";

interface PostShowSetupStateManager {
    init(boardId: string | null): void;
    getBoardId(): string | null;
}

interface PostShowSetupResult {
    stateManager: PostShowStateHandler;
    apiService: PostShowApiService;
    handler: PostShowHandler;
    listener: PostShowListener;
    pageReadRecorded?: boolean;
    pagePostId?: number;
}

class PostShowSetup {
    private static readonly READ_QUALIFICATION_MS = 10000;
    private static readonly READ_SCROLL_THRESHOLD_PX = 24;

    public static async init(): Promise<PostShowSetupResult> {
        if (postShowSetup) {
            return postShowSetup;
        }

        const boardId = this.getBoardId();

        const stateManager = new PostShowStateHandler();
        const apiService = new PostShowApiService();
        const handler = new PostShowHandler(stateManager, apiService);
        const listener = new PostShowListener(handler);

        stateManager.init(boardId);
        listener.setup();
        this.processModelessSnsPreviews();
        initAffiliateCopyButtons(document);

        const readResult = this.markPageAsRead(stateManager, apiService, handler, listener);
        postShowSetup = {
            stateManager,
            apiService,
            handler,
            listener,
            ...(readResult || {}),
        };

        return postShowSetup;
    }

    public static markPageAsRead(
        stateManager: PostShowSetupStateManager,
        apiService: PostShowApiService,
        handler: PostShowHandler,
        listener: PostShowListener,
    ): Partial<PostShowSetupResult> | undefined {
        const postContainer = postShowForm.getPostDetail(document);
        if (!(postContainer instanceof HTMLElement)) return;

        if (postContainer.dataset.pageReadPending === "true" || postContainer.dataset.pageReadRecorded === "true") {
            return;
        }
        postContainer.dataset.pageReadPending = "true";

        const postId = toPositiveIntOrNull(postContainer.dataset.postId);
        const boardId = stateManager.getBoardId();
        const postUserId = toPositiveIntOrNull(postContainer.dataset.userId);

        if (postId === null || !boardId) {
            delete postContainer.dataset.pageReadPending;
            return;
        }

        const currentUserId = Number(phpAuthUser()?.user_id || 0);
        if (currentUserId && postUserId !== null && currentUserId === postUserId) {
            delete postContainer.dataset.pageReadPending;
            return;
        }

        void this.recordQualifiedPageRead(postContainer, postId, boardId, apiService);

        return {
            stateManager: stateManager as PostShowStateHandler,
            apiService,
            handler,
            listener,
            pageReadRecorded: false,
            pagePostId: postId,
        };
    }

    private static async recordQualifiedPageRead(
        postContainer: HTMLElement,
        postId: number,
        boardId: string,
        apiService: PostShowApiService,
    ): Promise<void> {
        try {
            const qualified = await this.waitForPageReadQualification();
            if (!qualified || !document.body.contains(postContainer)) {
                return;
            }

            const result = await apiService.markAsRead(postId, boardId, false);

            if (result && typeof result === "object" && result.success) {
                postContainer.dataset.pageReadRecorded = "true";
                postContainer.dataset.pageReadPending = "false";
            }
        } catch (error) {
            console.warn("Failed to mark post as read:", error);
        } finally {
            if (postContainer.dataset.pageReadRecorded !== "true") {
                delete postContainer.dataset.pageReadPending;
            }
        }
    }

    private static waitForPageReadQualification(): Promise<boolean> {
        const scrollingElement = document.scrollingElement ?? document.documentElement;
        const initialScrollTop = scrollingElement?.scrollTop ?? 0;

        return new Promise<boolean>((resolve) => {
            let hasScrolled = initialScrollTop >= this.READ_SCROLL_THRESHOLD_PX;

            const handleScroll = (): void => {
                const currentScrollTop = scrollingElement?.scrollTop ?? 0;
                if (Math.abs(currentScrollTop - initialScrollTop) >= this.READ_SCROLL_THRESHOLD_PX) {
                    hasScrolled = true;
                }
            };

            bindPostShowPageReadScroll(handleScroll);

            window.setTimeout(() => {
                unbindPostShowPageReadScroll(handleScroll);
                resolve(hasScrolled);
            }, this.READ_QUALIFICATION_MS);
        });
    }

    private static processModelessSnsPreviews(): void {
        const postDetail = postShowForm.getPostDetail(document);
        if (!(postDetail instanceof HTMLElement)) {
            return;
        }

        const contentElement = postShowForm.getPostDetailContent(postDetail);
        if (!(contentElement instanceof HTMLElement)) {
            return;
        }

        contentElement.removeAttribute("data-sns-processed");
        SNSPreviewManagerSetup.getManager().processContentForDisplay(contentElement, false);

        // Process the media area icons only — no tile creation in detail view (SSR renders images at full scale).
        SNSPreviewManagerSetup.getManager().initForIndex(`[data-role="${Roles.POST_DETAIL}"]`, false);
    }

    public static getBoardId(): string | null {
        const postDetail = postShowForm.getPostDetail(document);
        if (postDetail instanceof HTMLElement && postDetail.dataset.boardId) {
            return postDetail.dataset.boardId;
        }

        const pathParts = window.location.pathname.split("/");
        const postsIndex = pathParts.findIndex((part) => part === "posts");
        if (postsIndex !== -1 && pathParts[postsIndex + 1]) {
            return pathParts[postsIndex + 1] || null;
        }

        const metaElement = postShowForm.getBoardIdMeta(document);
        if (metaElement instanceof HTMLMetaElement) {
            return metaElement.getAttribute("content");
        }

        const bodyElement = document.body;
        if (bodyElement.dataset.boardId) {
            return bodyElement.dataset.boardId;
        }

        return null;
    }
}

let postShowSetup: PostShowSetupResult | null = null;

function getPostShowSetup(): PostShowSetupResult | null {
    return postShowSetup;
}

/**
 * Lightweight post show action setup for pages that have dropdown containers
 * but are NOT the dedicated post show page (e.g. product detail with list cards).
 * Only registers the event listeners for edit/delete/share — no SNS processing or read tracking.
 */
async function initPostShowActions(): Promise<void> {
    if (postShowSetup) return;

    const boardId = PostShowSetup.getBoardId();
    const stateManager = new PostShowStateHandler();
    const apiService = new PostShowApiService();
    const handler = new PostShowHandler(stateManager, apiService);
    const listener = new PostShowListener(handler);

    stateManager.init(boardId);
    listener.setup();

    postShowSetup = { stateManager, apiService, handler, listener };
}

export { PostShowSetup, getPostShowSetup, initPostShowActions };
